import { Box } from "@chakra-ui/react";
import { ReactNode } from "react";

/**
 * FilterPill — `.filter-pill` from the redesign bundle.
 * design ref: styles.css:1023–1041, tasks.jsx:120
 *
 * Pill-shaped button with optional colored dot + optional trailing count.
 * Active state paints with azurite tint (`.active`).
 */
export interface FilterPillProps {
  active?: boolean;
  onClick?: () => void;
  /** Colored leading dot — pass a theme token or hex. */
  dotColor?: string;
  /** Optional trailing count. */
  count?: number;
  children: ReactNode;
}

const FilterPill = ({
  active = false,
  onClick,
  dotColor,
  count,
  children,
}: FilterPillProps) => {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="inline-flex"
      alignItems="center"
      gap="1xs" // 6px
      px="md" // 12px
      py="1xs" // 6px
      fontSize="smd" // 13px
      fontWeight={500}
      color={active ? "p.700" : "neutral.800"}
      bg={active ? "p.50" : "neutral.25"}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={active ? "p.300" : "neutral.300"}
      borderRadius="full" // 100px
      cursor="pointer"
      transition="background-color 120ms, border-color 120ms, color 120ms"
      _hover={
        active
          ? undefined
          : {
              bg: "neutral.100",
              borderColor: "neutral.400",
            }
      }
      _focusVisible={{
        outline: "none",
        boxShadow: "focusRing",
      }}
    >
      {dotColor && (
        <Box
          as="span"
          width="8px"
          height="8px"
          borderRadius="full"
          bg={dotColor}
          flexShrink={0}
          aria-hidden
        />
      )}
      <Box as="span">{children}</Box>
      {count !== undefined && (
        <Box
          as="span"
          color={active ? "p.700" : "neutral.700"}
          fontWeight={600}
        >
          {count}
        </Box>
      )}
    </Box>
  );
};

export default FilterPill;
